/**
 * Column definitions for the planning table.
 * Order is fully configurable via drag-and-drop on the headers.
 * Week# stays frozen as the leftmost column (it anchors every row).
 */

export const TASK_COLUMN_DEFS = {
  week: {
    id: "week",
    label: "Week#",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-300 font-black whitespace-nowrap bg-slate-800/60 border-r border-slate-700/40",
    frozen: true,           // cannot be moved
  },
  task: {
    id: "task",
    label: "Task",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black",
  },
  comments: {
    id: "comments",
    label: "Comments",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black min-w-[280px]",
  },
  start: {
    id: "start",
    label: "Start",
    title: "Earliest date the task can start (window start).",
    thClass: "px-2 py-1.5 w-14 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center whitespace-nowrap",
  },
  finish: {
    id: "finish",
    label: "Finish",
    title: "Latest date the task must be done by (deadline). Task can happen any day between Start and Finish; only Days worker-days are consumed from capacity.",
    thClass: "px-2 py-1.5 w-14 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center whitespace-nowrap",
  },
  members: {
    id: "members",
    label: "Members",
    title: "Number of team members assigned",
    thClass: "px-2 py-1.5 w-16 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center",
  },
  days: {
    id: "days",
    label: "Days",
    title: "Actual work days consumed (NOT the Start→Finish window). Ex: window Mon–Fri, but task needs only 2 work days → put 2.",
    thClass: "px-2 py-1.5 w-14 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center",
  },
  hours: {
    id: "hours",
    label: "Hours",
    title: "Total member-hours (defaults to members × days × 7.5h; edit to override)",
    thClass: "px-2 py-1.5 w-16 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center",
  },
  budget: {
    id: "budget",
    label: "Budget",
    title: "Budget = Hours × 600 NOK/h",
    thClass: "px-2 py-1.5 w-24 text-[10px] uppercase tracking-widest text-slate-400 font-black text-center",
  },
  status: {
    id: "status",
    label: "Status",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black",
  },
  team: {
    id: "team",
    label: "Team",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black",
  },
  actions: {
    id: "actions",
    label: "Actions",
    thClass: "px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black text-right",
  },
};

// Default order — Comments right after Task, per user preference.
export const DEFAULT_COLUMN_ORDER = [
  "week",
  "task",
  "comments",
  "start",
  "finish",
  "members",
  "days",
  "hours",
  "budget",
  "status",
  "team",
  "actions",
];

/** Sanitize a persisted hidden-columns list: drop unknown ids, dedupe, and
 *  never allow the frozen `week` column to be hidden. */
export function sanitizeHiddenColumns(hidden) {
  const known = new Set(Object.keys(TASK_COLUMN_DEFS));
  const out = [];
  const seen = new Set();
  for (const id of hidden || []) {
    if (id === "week") continue; // frozen — always visible
    if (known.has(id) && !seen.has(id)) { out.push(id); seen.add(id); }
  }
  return out;
}

/** Sanitize any persisted order, guaranteeing week is first (frozen) and every
 *  known column appears exactly once. Unknown ids are dropped. */
export function normalizeColumnOrder(order) {
  const known = new Set(Object.keys(TASK_COLUMN_DEFS));
  const seen = new Set();
  const out = ["week"]; // frozen
  seen.add("week");
  for (const id of order || []) {
    if (id === "week") continue;
    if (known.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  // Append any missing columns at the end (in default order).
  for (const id of DEFAULT_COLUMN_ORDER) {
    if (!seen.has(id)) out.push(id);
  }
  return out;
}
